'use client';

import { useEffect, useState, useRef } from 'react';
import Image from 'next/image';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { montserratSemi } from '../fonts';
import HomeSectionTitle from './home-section-title';
// Note: avoid SSR/device detection at render time to prevent hydration mismatches.

export default function MobileSection() {
  const t = useTranslations('home.mobile');
  // Evaluate viewport size on client after hydration to keep SSR markup stable.
  const [isMobileClient, setIsMobileClient] = useState(false);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobileClient(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], [100, -100]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 10]);
  const backY = useTransform(
    scrollYProgress,
    [0, 1],
    isMobileClient ? [100, -50] : [200, -50]
  );
  const backRotate = useTransform(scrollYProgress, [0, 1], [-5, -5]);

  const goToDownload = () => window.open('/download', '_self');

  return (
    <section ref={containerRef} className="py-32 bg-[color:var(--background)] overflow-hidden relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 md:gap-20 gap-0 items-center">
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="z-10"
        >
          <div className="mb-8">
            <HomeSectionTitle
              title={t('title')}
              description={t('description')}
              centered={false}
              containerClassName="text-left mb-12"
            />
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="hidden md:flex gap-4"
          >
            <button className={`${montserratSemi.className} px-8 py-4 bg-white text-black rounded-full hover:bg-gray-200 transition-colors cursor-pointer`} 
              onClick={goToDownload}>
              {t('iosApp')}
            </button>
            <button className={`${montserratSemi.className} px-8 py-4 bg-transparent border border-white/20 text-white rounded-full hover:bg-white/5 transition-colors cursor-pointer`} 
              onClick={goToDownload} >
              {t('androidApp')}
            </button>
          </motion.div>
        </motion.div>

        <div className="relative md:h-[800px] h-[350px] flex items-center justify-center" style={{ perspective: '1000px' }}>
           {/* Floating Phone */}
           <motion.div 
             style={{ y, rotateX: 10, rotateY: -20, rotateZ: rotate }}
             className="md:w-[350px] md:h-[700px] w-[175px] h-[350px] bg-black rounded-[3rem] border-8 border-[#1a1a1a] shadow-2xl relative overflow-hidden"
           >
             <Image
              src="/assets/mobile-demo-1.webp"
              alt={t('altText')}
              width={1079}
              height={2209}
              className="w-full h-full object-cover"
              loading="lazy"
            />
           </motion.div>
          
           {/* Back Phone */}
           <motion.div 
             style={{ 
               y: backY, 
               rotateX: 10, 
               rotateY: -20, 
               rotateZ: backRotate, 
               x: 50, 
               scale: 0.9, 
               opacity: 0.5 
             }}
             className="absolute md:w-[350px] md:h-[700px] w-[175px] h-[350px] bg-black rounded-[3rem] border-8 border-[#1a1a1a] -z-10 blur-[1px]"
           >
             <Image
              src="/assets/mobile-demo-2.webp"
              alt={t('altText')}
              width={1079}
              height={2209}
              loading="lazy"
              className="w-full h-full object-cover"
            />
           </motion.div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="justify-center md:hidden flex gap-4 pt-12"
        >
          <button className={`${montserratSemi.className} w-full px-8 py-4 bg-white text-black rounded-full hover:bg-gray-200 transition-colors cursor-pointer`} 
            onClick={goToDownload}>
            {t('iosApp')}
          </button>
          <button className={`${montserratSemi.className} w-full px-8 py-4 bg-transparent border border-white/20 text-white rounded-full hover:bg-white/5 transition-colors cursor-pointer`} 
            onClick={goToDownload} >
            {t('androidApp')}
          </button>
        </motion.div>
      </div>
    </section>
  );
}

